/**
 * PracticeCheckReminder — mounts once on the dashboard for admin/PM logins.
 *
 * Polls /api/users/licensing/practice-checks on first mount of the session
 * and surfaces a single Sonner toast summarising:
 *   • N overdue annual practice-checks (red)
 *   • N due in the next 14 days (amber)
 *
 * Shown **once per browser session** (sessionStorage flag) so the PM isn't
 * spammed every time they navigate. They can re-trigger by clearing the
 * key (or a hard browser restart). The toast links to the People page so
 * one click goes from the warning to the action.
 *
 * Hidden for non-PM logins — clinical staff don't action their own annual
 * check, so the reminder would be noise.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'i3_practice_check_reminder_shown_v1';

export default function PracticeCheckReminder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';

  useEffect(() => {
    if (!isPM) return;
    // Only run once per browser session.
    if (sessionStorage.getItem(SESSION_KEY) === '1') return;

    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/users/licensing/practice-checks?warn_days=14`,
          { withCredentials: true },
        );
        if (cancelled) return;
        const summary = r.data?.summary || {};
        const overdue = summary.overdue || 0;
        const dueSoon = summary.due_soon || 0;
        const never = summary.never || 0;
        if (overdue === 0 && dueSoon === 0 && never === 0) {
          sessionStorage.setItem(SESSION_KEY, '1');
          return;
        }
        const total = overdue + dueSoon + never;
        const lines = [];
        if (overdue) lines.push(`${overdue} overdue`);
        if (never) lines.push(`${never} never checked`);
        if (dueSoon) lines.push(`${dueSoon} due in next 14d`);
        const fn = overdue > 0 || never > 0 ? toast.error : toast.warning;
        fn(`${total} annual practice-check${total === 1 ? '' : 's'} need attention`, {
          description: lines.join(' · '),
          duration: 10000,
          action: {
            label: 'Open People',
            onClick: () => navigate('/people?tab=licensing'),
          },
          // Test hooks — Sonner doesn't render data-testid on the action
          // button itself, so we tag the toast root via id.
          id: 'practice-check-reminder',
        });
        sessionStorage.setItem(SESSION_KEY, '1');
      } catch {
        /* silent — non-blocking */
      }
    })();

    return () => { cancelled = true; };
  }, [isPM, navigate]);

  return null;
}
